(() => {
  'use strict';

  // Makes a whole product card clickable, for every homepage row at once:
  // the cloth rows, Table Rail Rubber, the cue strips, the case tiles, Cue
  // Tips, Pool Ball Sets and Single Balls. Those rows ship six unrelated
  // card classes, but each card already carries the shared `bc-card` marker
  // (added for the hover state in snippets/bc-layout-tokens.liquid), so one
  // module covers all of them and none of the six section files needs its
  // markup rewritten.
  //
  // HOW THE TARGET IS CHOSEN
  //
  // The overlay does not carry a URL of its own - it mirrors the card's BUY
  // button. That is deliberate: on the cards with variants (cloth sizes and
  // colours, cue variants) the BUY href is already kept pointing at the
  // SELECTED variant by simonis-family-size-selector.js, cloth-color-
  // previews.js and longoni-lightbox.js. Mirroring it means the card and its
  // button always lead to the same place, with no second copy of the
  // variant logic here to fall out of step. Cards whose BUY never changes
  // simply mirror a constant.
  //
  // A MutationObserver on the button's href keeps the overlay in step when a
  // size or colour is picked. Cloth cards hold one panel per size, all but
  // one hidden, so the VISIBLE button is the one that counts - hidden panels
  // are skipped when picking, and re-picked whenever the card mutates.
  //
  // WHY AN OVERLAY AND NOT A WRAPPER
  //
  // Wrapping the card in an <a> would nest the BUY link, the loupe and the
  // swatches inside a link, which is invalid and unusable with a keyboard.
  // The overlay is a SIBLING of the card's content: it sits above the inert
  // parts (photo, name, price) and below every control, so a click on empty
  // card space opens the product while the controls keep their own
  // behaviour. Nothing here is nested in anything.
  //
  // Z-INDEX IS SET PER ELEMENT, NOT BY A BLANKET RULE
  //
  // z-index only applies to positioned elements, so raising the controls
  // needs position other than static. A blanket ".bc-card button { position:
  // relative }" would have broken the cue tiles, whose BUY is
  // position:absolute in the corner - relative would drop it back into flow.
  // So each control is checked at runtime and only STATIC ones are made
  // relative; anything already positioned keeps the position it has and just
  // gets the z-index.

  if (window.BCCardLink) return;

  const CARD = '.bc-card';
  const LINK_CLASS = 'bc-card-link';

  // Where a card's own "go to the product" URL comes from. All of these are
  // the row's BUY control.
  const BUY = [
    '.cloth-size-card__buy',
    '.case-tile__buy',
  '.bc-shaft-tile__buy',
    '.rail-rubber-card__buy',
    '.brand-promo__button',
    '.longoni-promo-strip__button',
    // the My Offers panel's cards, which are bc-cards too
    '.my-offers__card-button'
  ].join(',');

  // Every control that must keep its own click. Deliberately NOT a blanket
  // "a" - the cards also carry plain links to the product on the photo and
  // on the name, and those point at the BASE product. Leaving them under
  // the overlay is what makes a click anywhere on a cloth or cue card open
  // the SELECTED variant, which is what the BUY button does; raising them
  // would have made the photo and the title disagree with the button on
  // the same card. They stay in the markup, and in the tab order, as the
  // no-JS fallback.
  const CONTROLS = [
    BUY,
    'button', 'input', 'select', 'textarea', 'label',
    '[role="button"]',
    // The two cloth size-guide entries that stood here went with the guide
    // itself (2026-10-01). Its replacement, .bc-cloth-tip, is deliberately
    // NOT listed: the tip is pointer-events:none and must stay under this
    // overlay, so a tap during the tip reaches the chip or swatch below it.
    '[data-longoni-gallery-trigger]',
    // THE SWATCH GRIDS THEMSELVES (2026-09-24). A gap between two swatches
    // must belong to the grid, not to the overlay under it: with only the
    // swatches lifted, the pointer in a gap stood on the overlay, pointerout
    // of a swatch read as leaving the grid, and the preview flickered back
    // to the selection on every gap (measured on the Vaula Pro card: six
    // reverts in one sweep). Lifting the grid puts its gaps in its own box;
    // a click in a gap still opens the product through the card's click
    // handler below. See assets/bc-hover-preview.js, onZoneLeave.
    '[data-cloth-color-options]', '[data-simonis-size-rail]', '[data-glove-swatches]', '[data-glove-hands]', '[data-rr-swatches]', '[data-shaft-chips]'
  ].join(',');

  const observed = new WeakMap();

  function visible(el) {
    return Boolean(el && el.offsetParent !== null);
  }

  // The BUY control that is actually on screen. Cloth cards carry one panel
  // per size with only the selected one displayed, so an offsetParent test
  // is what separates "the current variant's button" from five stale ones.
  function buyControl(card) {
    const all = card.querySelectorAll(BUY);
    let fallback = null;
    for (const el of all) {
      if (!fallback) fallback = el;
      if (visible(el)) return el;
    }
    return fallback;
  }

  function labelFor(card, buy) {
    // The product name as the card prints it, so the overlay reads as the
    // product rather than as "link".
    const named = card.querySelector(
      '.case-tile__model, .rail-rubber-card__model, .cloth-size-card__title,' +
      '.simonis-family-card__title, .longoni-promo-strip__model, .longoni-promo-strip__title,' +
      // the offer's own headline, so the overlay reads "Free Shipping"
      // rather than "Shop Now"
      '.my-offers__card-title'
    );
    const text = named && named.textContent ? named.textContent.trim() : '';
    if (text) return text;
    if (buy) {
      const aria = buy.getAttribute('aria-label');
      if (aria) return aria.replace(/^\s*(BUY|VIEW)\s+/i, '').trim() || aria;
      const own = (buy.textContent || '').trim();
      if (own) return own.replace(/^\s*(BUY|VIEW)\s+/i, '').trim() || own;
    }
    return '';
  }

  // Raise one control above the overlay without disturbing how it is laid
  // out. Only a static element is made relative; an absolutely positioned
  // BUY keeps its corner.
  // The overlay stays LOW on purpose - see the click handler below for why
  // raising it does not work. It has to lose to the cloth cards' own
  // .simonis-family-card__size-panels (position:relative, z-index:1), which
  // is a stacking context holding the photo, the swatches AND the BUY
  // button together. Lifting the overlay over that panel covered the
  // swatches and the button with it, because a control's own z-index is
  // resolved INSIDE its panel and cannot escape it. Measured: at overlay
  // z-index 4 the Simonis card's buy and swatch both stopped receiving
  // clicks.
  const OVERLAY_Z = '1';
  const CONTROL_Z = '2';

  function raise(el) {
    if (el.dataset.bcCardRaised === '1') return;
    let position = 'static';
    let zIndex = 'auto';
    try {
      const cs = window.getComputedStyle(el);
      position = cs.position;
      zIndex = cs.zIndex;
    } catch (error) {
      /* detached node - it will be raised on the next pass */
    }
    if (position === 'static') el.style.position = 'relative';
    // Only lift a control that has no layer of its own. The cue tiles' BUY
    // is already at z-index 3 and writing 2 over it would push it DOWN,
    // under whatever its row stacks at 3.
    if (zIndex === 'auto' || zIndex === '0') el.style.zIndex = CONTROL_Z;
    el.dataset.bcCardRaised = '1';
  }

  function raiseAll(card) {
    card.querySelectorAll(CONTROLS).forEach((el) => {
      if (el.classList.contains(LINK_CLASS)) return;
      raise(el);
    });
  }

  function sync(card, overlay) {
    const buy = buyControl(card);
    // The BUY button first, so a card with variants always follows the
    // selected one; data-bc-card-href is the fallback for a card that has
    // no BUY link to mirror - a sold-out set renders a plain <span>.
    const href = (buy && buy.getAttribute('href')) || card.getAttribute('data-bc-card-href') || null;
    if (href) {
      if (overlay.getAttribute('href') !== href) overlay.setAttribute('href', href);
      overlay.removeAttribute('aria-hidden');
    } else {
      // No destination: keep the overlay inert rather than linking nowhere.
      overlay.removeAttribute('href');
      overlay.setAttribute('aria-hidden', 'true');
    }
    const label = labelFor(card, buy);
    if (label && overlay.getAttribute('aria-label') !== label) {
      overlay.setAttribute('aria-label', label);
    }
  }

  function attach(card) {
    if (!(card instanceof Element)) return;
    if (card.querySelector(':scope > .' + LINK_CLASS)) {
      raiseAll(card);
      return;
    }

    if (!buyControl(card) && !card.getAttribute('data-bc-card-href')) return;

    const overlay = document.createElement('a');
    overlay.className = LINK_CLASS;
    overlay.setAttribute('data-bc-card-link', '');
    // Inline, so it beats the stylesheet's default without the sheet having
    // to know what the rows layer their own content at.
    overlay.style.zIndex = OVERLAY_Z;
    // First child, so it paints under the card's own content in source
    // order and the z-index below is what puts it over the inert parts.
    card.insertBefore(overlay, card.firstChild);

    raiseAll(card);
    sync(card, overlay);

    // The overlay alone cannot cover a card whose content sits in its own
    // stacking context - on the cloth cards the photo, the swatches and the
    // BUY share one panel at z-index 1, so an overlay high enough to cover
    // the photo also covers the two controls. This handler closes that gap
    // from the other side: a click that did not land on a control, a link,
    // or inside a text selection opens the same URL the overlay carries.
    //
    // Not a replacement for the overlay - that stays, and is what gives the
    // card a real link: a keyboard tab stop, an accessible name, middle-
    // click, and "open in new tab" from the context menu.
    card.addEventListener('click', (event) => {
      if (event.defaultPrevented) return;
      // Let a modified click keep its normal meaning on whatever it hit.
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (typeof event.button === 'number' && event.button !== 0) return;

      const target = event.target;
      if (!(target instanceof Element)) return;
      // Anything that is already interactive handles its own click.
      if (target.closest('a, button, input, select, textarea, label, [role="button"], [role="link"]')) return;
      // A drag to select text is not a click on the card.
      const selection = window.getSelection && window.getSelection();
      if (selection && String(selection).length > 0) return;

      const href = overlay.getAttribute('href');
      if (!href) return;
      window.location.href = href;
    });

    // Follow the BUY href as variants change, and pick up controls that
    // arrive later (a lazily rendered panel, a swatch strip).
    if (!observed.has(card)) {
      const observer = new MutationObserver(() => {
        raiseAll(card);
        sync(card, overlay);
      });
      observer.observe(card, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['href', 'style', 'class', 'hidden']
      });
      observed.set(card, observer);
    }
  }

  function scan(root) {
    const scope = root instanceof Element || root instanceof Document ? root : document;
    if (scope instanceof Element && scope.matches && scope.matches(CARD)) attach(scope);
    scope.querySelectorAll(CARD).forEach(attach);
  }

  function boot() {
    scan(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  // The homepage rows are fetched after load through the Section Rendering
  // API and injected by their slot hosts, which dispatch this on the new
  // root. Scripts inside a fetched section never execute, so this listener
  // in the layout is what covers them.
  document.addEventListener('shopify:section:load', (event) => {
    scan(event.target || document);
  });

  // Safety net for anything injected without that event.
  if (window.MutationObserver) {
    const pageObserver = new MutationObserver((records) => {
      for (const record of records) {
        for (const node of record.addedNodes) {
          if (node instanceof Element) scan(node);
        }
      }
    });
    pageObserver.observe(document.documentElement, { childList: true, subtree: true });
  }

  window.BCCardLink = { scan, attach, CARD, LINK_CLASS };
})();
